import { useEffect } from 'react'

import {
  sorteeLandingSeo,
  type SorteeLandingSeoContent,
} from '../content/sorteeLandingContent'

const SEO_DATASET = 'sorteeLandingSeo'

function appendMeta(
  headElement: HTMLHeadElement,
  attr: 'name' | 'property',
  key: string,
  value: string
): HTMLMetaElement {
  const metaElement = document.createElement('meta')
  metaElement.setAttribute(attr, key)
  metaElement.content = value
  metaElement.dataset[SEO_DATASET] = '1'
  headElement.appendChild(metaElement)
  return metaElement
}

function appendLink(
  headElement: HTMLHeadElement,
  rel: string,
  href: string,
  extraAttributes?: Record<string, string>
): HTMLLinkElement {
  const linkElement = document.createElement('link')
  linkElement.rel = rel
  linkElement.href = href
  if (extraAttributes) {
    Object.entries(extraAttributes).forEach(([key, value]) => {
      linkElement.setAttribute(key, value)
    })
  }
  linkElement.dataset[SEO_DATASET] = '1'
  headElement.appendChild(linkElement)
  return linkElement
}

export function SorteeLandingSeo({
  content = sorteeLandingSeo,
}: {
  content?: SorteeLandingSeoContent
}) {
  useEffect(() => {
    const previousTitle = document.title
    document.title = content.browserTitle

    const headElement = document.head
    const createdNodes: HTMLElement[] = []

    createdNodes.push(
      appendMeta(headElement, 'name', 'description', content.description),
      appendMeta(headElement, 'name', 'keywords', content.keywords),
      appendMeta(headElement, 'name', 'author', content.author),
      appendMeta(headElement, 'name', 'robots', content.robots),
      appendMeta(headElement, 'name', 'theme-color', content.themeColor),
      appendMeta(headElement, 'property', 'og:type', content.og.type),
      appendMeta(headElement, 'property', 'og:url', content.og.url),
      appendMeta(headElement, 'property', 'og:title', content.og.title),
      appendMeta(headElement, 'property', 'og:description', content.og.description),
      appendMeta(headElement, 'property', 'og:image', content.og.image),
      appendMeta(headElement, 'property', 'og:image:width', content.og.imageWidth),
      appendMeta(headElement, 'property', 'og:image:height', content.og.imageHeight),
      appendMeta(headElement, 'property', 'og:locale', content.og.locale),
      appendMeta(headElement, 'property', 'og:site_name', content.og.siteName),
      appendMeta(headElement, 'name', 'twitter:card', content.twitter.card),
      appendMeta(headElement, 'name', 'twitter:site', content.twitter.site),
      appendMeta(headElement, 'name', 'twitter:title', content.twitter.title),
      appendMeta(headElement, 'name', 'twitter:description', content.twitter.description),
      appendMeta(headElement, 'name', 'twitter:image', content.twitter.image)
    )

    createdNodes.push(
      appendLink(headElement, 'canonical', content.canonical),
      appendLink(headElement, 'manifest', '/manifest.json'),
      appendLink(headElement, 'icon', '/favicon.svg', { type: 'image/svg+xml' }),
      appendLink(headElement, 'apple-touch-icon', '/apple-touch-icon.png', {
        sizes: '180x180',
      }),
      appendLink(headElement, 'preconnect', 'https://fonts.googleapis.com'),
      appendLink(headElement, 'preconnect', 'https://fonts.gstatic.com', {
        crossorigin: '',
      }),
      appendLink(
        headElement,
        'stylesheet',
        'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap'
      )
    )

    const jsonLdScript = document.createElement('script')
    jsonLdScript.type = 'application/ld+json'
    jsonLdScript.text = JSON.stringify(content.jsonLd)
    jsonLdScript.dataset[SEO_DATASET] = '1'
    headElement.appendChild(jsonLdScript)
    createdNodes.push(jsonLdScript)

    return () => {
      document.title = previousTitle
      createdNodes.forEach((node) => {
        if (node.isConnected) {
          node.remove()
        }
      })
    }
  }, [content])

  return null
}
